import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Banknote, RefreshCw, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

/**
 * End-of-shift Cash-Out widget for the office manager.
 * Shows, at a glance, every driver who still has physical cash on hand from
 * cash-paid rides so nobody knocks off with the cash box still full.
 */
export default function CashOutTab() {
  const [data, setData] = useState({ rows: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/cash-out");
      setData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const outstanding = data.totals?.outstanding || 0;
  const holders = data.rows.filter((r) => (r.on_hand || 0) > 0.01);

  return (
    <div data-testid="cash-out-tab">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-caption">Cash reconciliation</div>
          <h1 className="font-display text-3xl font-extrabold">Cash-Out</h1>
          <p className="text-slate-400 text-sm mt-1">Who's still holding cash from cash-paid rides. Refreshes every 60 seconds.</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => { setRefreshing(true); load(); }}
          className="bg-transparent border-white/10 h-8" data-testid="cash-out-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="glass-card rounded-2xl p-8 flex items-center gap-2 text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-3 gap-3 mb-6" data-testid="cash-out-totals">
            <StatTile label="Outstanding" value={outstanding} tone="red" testid="cash-out-outstanding" icon={AlertTriangle} sub={`${data.totals?.drivers_with_cash || 0} driver${(data.totals?.drivers_with_cash || 0) === 1 ? "" : "s"}`} />
            <StatTile label="Collected (all time)" value={data.totals?.collected || 0} tone="amber" testid="cash-out-collected" icon={Banknote} sub={`Corp NZ$${(data.totals?.corporate_cash || 0).toFixed(2)} · Walk-in NZ$${(data.totals?.walk_in_cash || 0).toFixed(2)}`} />
            <StatTile label="Settled (all time)" value={data.totals?.settled || 0} tone="emerald" testid="cash-out-settled" icon={Banknote} />
          </div>

          {holders.length === 0 ? (
            <div className="glass-card gold-border rounded-2xl p-10 text-center" data-testid="cash-out-empty">
              <Banknote className="w-8 h-8 mx-auto text-emerald-400/60 mb-2" />
              <p className="text-slate-300 font-display text-lg">All cash reconciled.</p>
              <p className="text-slate-500 text-sm mt-1">No driver is currently holding cash. Nice.</p>
            </div>
          ) : (
            <div className="glass-card gold-border rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-black/30 border-b border-white/5">
                    <tr className="text-[10px] uppercase tracking-widest font-mono text-slate-400">
                      <th className="text-left px-4 py-3">Driver</th>
                      <th className="text-left px-4 py-3">Status</th>
                      <th className="text-right px-4 py-3">Corporate</th>
                      <th className="text-right px-4 py-3">Walk-in</th>
                      <th className="text-right px-4 py-3">Settled</th>
                      <th className="text-right px-4 py-3">On hand</th>
                      <th className="text-left px-4 py-3">Last activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {holders.map((r) => (
                      <tr key={r.driver_id} className="border-b border-white/5 hover:bg-black/20" data-testid={`cash-out-row-${r.driver_id}`}>
                        <td className="px-4 py-3">
                          <div className="text-white font-medium">{r.name || r.driver_id}</div>
                          <div className="text-[11px] text-slate-500 font-mono">{r.vehicle} {r.plate}</div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={`${r.status === "on_shift" || r.status === "available" ? "bg-emerald-400/10 border-emerald-400/30 text-emerald-300" : "bg-slate-400/10 border-slate-400/20 text-slate-400"} border font-mono uppercase text-[10px] tracking-wider`}>
                            {(r.status || "offline").replace("_", " ")}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right font-mono">
                          <span className="text-indigo-300" data-testid={`cash-out-corp-${r.driver_id}`}>NZ$ {(r.corporate_cash || 0).toFixed(2)}</span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono">
                          <span className="text-slate-300" data-testid={`cash-out-walkin-${r.driver_id}`}>NZ$ {(r.walk_in_cash || 0).toFixed(2)}</span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-slate-500">NZ$ {r.settled.toFixed(2)}</td>
                        <td className="px-4 py-3 text-right font-mono">
                          <span className={`font-bold text-lg ${r.on_hand > 100 ? "text-red-400" : "text-amber-400"}`} data-testid={`cash-out-onhand-${r.driver_id}`}>NZ$ {r.on_hand.toFixed(2)}</span>
                        </td>
                        <td className="px-4 py-3 font-mono text-[11px] text-slate-500">
                          {r.last_activity ? new Date(r.last_activity).toLocaleString() : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const StatTile = ({ label, value, tone, testid, icon: Icon, sub }) => {
  const toneMap = {
    red:     "bg-red-500/10 border-red-400/30 text-red-300",
    amber:   "bg-amber-400/10 border-amber-400/30 text-amber-300",
    emerald: "bg-emerald-400/10 border-emerald-400/30 text-emerald-300",
  };
  return (
    <div className={`glass-card rounded-2xl p-5 border ${toneMap[tone] || ""}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-mono opacity-80">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="mt-2 font-display text-3xl font-extrabold text-white">NZ$ {Number(value).toFixed(2)}</div>
      {sub && <div className="text-xs text-slate-400 mt-1 font-mono">{sub}</div>}
    </div>
  );
};
